import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import './HROverview.css';

const DEPT_COLORS = {
  'Sales':            '#3b82f6',
  'Accounts':         '#10b981',
  'Tech / Marketing': '#8b5cf6',
  'Warehouse':        '#f59e0b',
  'Unassigned':       '#94a3b8',
  'Driver':           '#06b6d4',
  'Incharge':         '#f97316',
};

const CITY_CONFIG = {
  Lahore:    { color: '#3b82f6', bg: '#eff6ff' },
  Karachi:   { color: '#10b981', bg: '#f0fdf4' },
  Islamabad: { color: '#8b5cf6', bg: '#f5f3ff' },
  Multan:    { color: '#f97316', bg: '#fff7ed' },
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['ov-av-blue','ov-av-teal','ov-av-amber','ov-av-purple','ov-av-coral','ov-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatPKR(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `PKR ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `PKR ${(n/1000).toFixed(0)}K`;
  return `PKR ${Number(n).toLocaleString('en-PK')}`;
}

function formatTime() {
  return new Date().toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function formatShortDate(d) {
  return new Date(d).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric' });
}

// Animated counter
function AnimatedNumber({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (start === end) { setDisplay(end); return; }
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(timer); }
      else setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{display}</span>;
}

// Circular gauge
function AttendanceGauge({ rate }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (rate / 100) * circ;
  const color = rate >= 80 ? '#10b981' : rate >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="ov-gauge-wrap">
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#f1f5f9" strokeWidth="12" />
        <circle
          cx="70" cy="70" r={r} fill="none"
          stroke={color} strokeWidth="12"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 70 70)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="70" y="64" textAnchor="middle" fontSize="26" fontWeight="700" fill="#0f172a">
          {rate}%
        </text>
        <text x="70" y="82" textAnchor="middle" fontSize="11" fill="#94a3b8">
          Attendance
        </text>
      </svg>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ov-tooltip">
      <div className="ov-tooltip-label">{formatShortDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} className="ov-tooltip-row" style={{ color: p.color }}>
          <span>{p.name}:</span><span>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function HROverview() {
  const [time, setTime]             = useState(formatTime());
  const [todayData, setTodayData]   = useState(null);
  const [trend, setTrend]           = useState([]);
  const [departments, setDepartments] = useState([]);
  const [alerts, setAlerts]         = useState(null);
  const [employees, setEmployees]   = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setTime(formatTime()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const now   = new Date();
      const month = now.getMonth() + 1;
      const year  = now.getFullYear();
      const [todayRes, trendRes, deptRes, alertsRes, empRes, salRes] = await Promise.all([
        api.getAllAttendance(),
        api.getAttendanceTrend(14),
        api.getDepartmentStats(),
        api.getAlerts(),
        api.getAllEmployees(),
        api.getAllSalaryReports({ month, year }),
      ]);
      setTodayData(todayRes);
      setTrend(trendRes.trend || []);
      setDepartments(deptRes.departments || []);
      setAlerts(alertsRes);
      setEmployees(empRes.employees || []);
      setSalaryData(salRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 120000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const totalEmployees = employees.length;
  const clockedIn      = todayData?.summary?.clocked_in  || 0;
  const clockedOut     = todayData?.summary?.clocked_out || 0;
  const lateToday      = todayData?.summary?.late        || 0;
  const totalPresent   = todayData?.summary?.total       || 0;
  const absentToday    = Math.max(0, totalEmployees - totalPresent);
  const attendanceRate = totalEmployees > 0 ? Math.round((totalPresent / totalEmployees) * 100) : 0;
  const totalDeductions = parseFloat(salaryData?.summary?.total_deductions || 0);
  const totalNetSalary  = parseFloat(salaryData?.summary?.total_net_salary  || 0);
  const lateAlertCount  = alerts?.late_alerts?.length || 0;

  // Group employees by city
  const cityStats = ['Lahore','Karachi','Islamabad','Multan'].map(city => {
    const cityEmps    = employees.filter(e => e.city === city);
    const cityPresent = (todayData?.records || []).filter(r => r.city === city).length;
    return { city, total: cityEmps.length, present: cityPresent };
  });

  // Trend chart data
  const trendData = trend.slice(-14).map(t => ({
    date:    t.date,
    Present: t.present_count,
    Late:    t.late_count,
    Rate:    t.attendance_rate,
  }));

  // Department pie
  const pieData = departments.map(d => ({
    name:  d.department,
    value: parseInt(d.total),
    present: parseInt(d.present_today),
  })).filter(d => d.value > 0);

  const now = new Date();

  if (loading) {
    return (
      <div className="ov-loading">
        <div className="ov-spinner" />
        <p>Loading mission control…</p>
      </div>
    );
  }

  return (
    <div className="ov-root">

      {/* TOP BAR */}
      <div className="ov-topbar">
        <div className="ov-topbar-left">
          <div className="ov-title-row">
            <h1 className="ov-title">Mission Control</h1>
            <span className="ov-live-badge">● LIVE</span>
          </div>
          <span className="ov-date">
            {now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="ov-topbar-right">
          <div className="ov-clock-box">
            <span className="ov-clock">{time}</span>
            <span className="ov-tz">PKT</span>
          </div>
          {lastRefresh && (
            <span className="ov-refresh-time">
              Refreshed {lastRefresh.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          )}
          <button className="ov-refresh-btn" onClick={fetchAll}>↻ Refresh</button>
        </div>
      </div>

      {/* ALERTS BANNER */}
      {lateAlertCount > 0 && (
        <div className="ov-alert-banner">
          <span className="ov-alert-icon">⚠</span>
          <span>{lateAlertCount} employee{lateAlertCount > 1 ? 's' : ''} have reached 3+ late arrivals this month — salary deduction will apply</span>
          <span className="ov-alert-action">Review in Reports →</span>
        </div>
      )}

      {/* MAIN GRID */}
      <div className="ov-main-grid">

        {/* LEFT COLUMN */}
        <div className="ov-left-col">

          {/* KPI STRIP */}
          <div className="ov-kpi-strip">
            <div className="ov-kpi-card ov-kpi-blue">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Total Employees</span>
                <span className="ov-kpi-icon-sm">👥</span>
              </div>
              <div className="ov-kpi-value"><AnimatedNumber value={totalEmployees} /></div>
              <div className="ov-kpi-sub">Active workforce</div>
            </div>
            <div className="ov-kpi-card ov-kpi-green">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Currently In</span>
                <span className="ov-kpi-pulse-dot" />
              </div>
              <div className="ov-kpi-value"><AnimatedNumber value={clockedIn} /></div>
              <div className="ov-kpi-sub">{clockedOut} clocked out</div>
            </div>
            <div className="ov-kpi-card ov-kpi-red">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Absent Today</span>
                <span className="ov-kpi-icon-sm">✗</span>
              </div>
              <div className="ov-kpi-value"><AnimatedNumber value={absentToday} /></div>
              <div className="ov-kpi-sub">Not clocked in</div>
            </div>
            <div className="ov-kpi-card ov-kpi-amber">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Late Today</span>
                <span className="ov-kpi-icon-sm">⏰</span>
              </div>
              <div className="ov-kpi-value"><AnimatedNumber value={lateToday} /></div>
              <div className="ov-kpi-sub">Past grace period</div>
            </div>
            <div className="ov-kpi-card ov-kpi-purple">
              <div className="ov-kpi-top">
                <span className="ov-kpi-label">Net Payroll</span>
                <span className="ov-kpi-icon-sm">₨</span>
              </div>
              <div className="ov-kpi-value ov-kpi-value-sm">{formatPKR(totalNetSalary)}</div>
              <div className="ov-kpi-sub ov-kpi-sub-red">
                {formatPKR(totalDeductions)} deducted
              </div>
            </div>
          </div>

          {/* ATTENDANCE TREND */}
          <div className="ov-card">
            <div className="ov-card-header">
              <div>
                <div className="ov-card-title">14-Day Attendance Trend</div>
                <div className="ov-card-sub">Daily present count and late arrivals</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="presentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="lateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="Present" stroke="#3b82f6" strokeWidth={2}
                  fill="url(#presentGrad)" dot={false} />
                <Area type="monotone" dataKey="Late" stroke="#f59e0b" strokeWidth={2}
                  fill="url(#lateGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* CITY BREAKDOWN */}
          <div className="ov-city-grid">
            {cityStats.map(({ city, total, present }) => {
              const cfg  = CITY_CONFIG[city] || { color: '#94a3b8', bg: '#f8fafc' };
              const rate = total > 0 ? Math.round((present / total) * 100) : 0;
              return (
                <div key={city} className="ov-city-card" style={{ borderTopColor: cfg.color }}>
                  <div className="ov-city-name" style={{ color: cfg.color }}>{city}</div>
                  <div className="ov-city-present">{present}<span>/{total}</span></div>
                  <div className="ov-city-bar">
                    <div className="ov-city-fill" style={{ width: `${rate}%`, background: cfg.color }} />
                  </div>
                  <div className="ov-city-rate">{rate}% present</div>
                </div>
              );
            })}
          </div>

        </div>

        {/* RIGHT COLUMN */}
        <div className="ov-right-col">

          {/* ATTENDANCE GAUGE */}
          <div className="ov-card ov-gauge-card">
            <div className="ov-card-title" style={{ marginBottom: 8 }}>Today's Rate</div>
            <AttendanceGauge rate={attendanceRate} />
            <div className="ov-gauge-stats">
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-green">{totalPresent}</span>
                <span className="ov-gauge-lbl">Present</span>
              </div>
              <div className="ov-gauge-divider" />
              <div className="ov-gauge-stat">
                <span className="ov-gauge-num ov-red">{absentToday}</span>
                <span className="ov-gauge-lbl">Absent</span>
              </div>
            </div>
          </div>

          {/* DEPARTMENT BREAKDOWN */}
          <div className="ov-card">
            <div className="ov-card-title" style={{ marginBottom: 12 }}>By Department</div>
            {departments.filter(d => parseInt(d.total) > 0).map((d, i) => {
              const color   = DEPT_COLORS[d.department] || '#94a3b8';
              const total   = parseInt(d.total);
              const present = parseInt(d.present_today);
              const rate    = total > 0 ? Math.round((present / total) * 100) : 0;
              return (
                <div key={i} className="ov-dept-row">
                  <div className="ov-dept-info">
                    <span className="ov-dept-dot" style={{ background: color }} />
                    <span className="ov-dept-name">{d.department || 'Unassigned'}</span>
                  </div>
                  <div className="ov-dept-bar-wrap">
                    <div className="ov-dept-bar">
                      <div className="ov-dept-fill" style={{ width: `${rate}%`, background: color }} />
                    </div>
                    <span className="ov-dept-stat">{present}/{total}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ALERTS */}
          <div className="ov-card ov-alerts-card">
            <div className="ov-card-title" style={{ marginBottom: 10 }}>
              Alerts
              {lateAlertCount > 0 && (
                <span className="ov-alert-count">{lateAlertCount}</span>
              )}
            </div>
            {lateAlertCount === 0 && !alerts?.consecutive_absent?.length ? (
              <div className="ov-no-alerts">✓ All clear — no alerts today</div>
            ) : (
              <div className="ov-alerts-list">
                {alerts?.late_alerts?.slice(0, 4).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-amber">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.late_count} lates · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-amber">
                      {emp.late_count >= 6 ? 'Full day' : 'Half day'}
                    </span>
                  </div>
                ))}
                {alerts?.consecutive_absent?.slice(0, 3).map((emp, i) => (
                  <div key={i} className="ov-alert-row ov-alert-red">
                    <div className={`ov-alert-av ${getAvatarClass(emp.full_name)}`}>
                      {getInitials(emp.full_name)}
                    </div>
                    <div className="ov-alert-info">
                      <span className="ov-alert-name">{emp.full_name}</span>
                      <span className="ov-alert-detail">{emp.absent_days} absences · {emp.city}</span>
                    </div>
                    <span className="ov-alert-tag ov-tag-red">Absent</span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ABSENT TODAY GRID */}
      {alerts?.absent_today?.length > 0 && (
        <div className="ov-card ov-absent-section">
          <div className="ov-card-header">
            <div>
              <div className="ov-card-title">Not Clocked In Today</div>
              <div className="ov-card-sub">{alerts.absent_today.length} employees haven't clocked in</div>
            </div>
          </div>
          <div className="ov-absent-grid">
            {alerts.absent_today.map((emp, i) => (
              <div key={i} className="ov-absent-card">
                <div className={`ov-absent-av ${getAvatarClass(emp.full_name)}`}>
                  {getInitials(emp.full_name)}
                </div>
                <div className="ov-absent-info">
                  <span className="ov-absent-name">{emp.full_name}</span>
                  <span className="ov-absent-meta">
                    {emp.employee_id} · {emp.department || '—'} · {emp.city}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}